import {
  ReadOnlySelectorFamilyOptions,
  ReadOnlySelectorOptions,
  RecoilValueReadOnly,
  selector,
  selectorFamily,
  SerializableParam,
} from "recoil";

/**
 * Recoil 0.7.7 gives every selector a cache with `{ equality: "reference", eviction: "keep-all" }`: the
 * cache key is the raw dependency value and nothing is ever pruned, so a selector over a grid row array
 * keeps every array that atom has ever held alive for the whole session.
 *
 * Two entries rather than one because grouped grids alternate between two dependency states while groups
 * are expanded and collapsed, and a single entry would thrash.
 */
const BOUNDED_CACHE_POLICY = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  cachePolicy_UNSTABLE: { eviction: "lru", maxSize: 2 },
} as const;

/**
 * `selector` with a bounded cache. Use it for anything whose dependencies include a grid row array, a
 * grouped-rows map, or another structure rebuilt from them on every refresh.
 *
 * Use it along the whole path, not only where the atom is read: a value stays reachable while any one
 * cache still keys on it, so bounding a consumer does nothing if its producer keeps the default.
 */
export const boundedSelector = <T>(
  options: ReadOnlySelectorOptions<T>,
): RecoilValueReadOnly<T> =>
  selector<T>({ ...options, ...BOUNDED_CACHE_POLICY });

/**
 * `selectorFamily` with a bounded cache — see {@link boundedSelector}. The bound is per family
 * parameter, and it bounds each instance's cache, *not* how many instances exist: Recoil 0.7.7 builds
 * the family's parameter cache with `eviction: "keep-all"` hardcoded, and `cachePolicyForParams_UNSTABLE`
 * only controls its equality. A family whose parameter has open-ended cardinality therefore keeps one
 * instance per parameter ever seen, for the life of the `RecoilRoot`. Prefer one selector returning a
 * keyed map over a family parameterised by something unbounded.
 */
export const boundedSelectorFamily = <T, TParam extends SerializableParam>(
  options: ReadOnlySelectorFamilyOptions<T, TParam>,
): ((param: TParam) => RecoilValueReadOnly<T>) =>
  selectorFamily<T, TParam>({ ...options, ...BOUNDED_CACHE_POLICY });
